import axios from "axios";
import { toast } from "react-toastify";

// Create axios instance with base config
const axiosInstance = axios.create({
  timeout: 300000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple token refresh requests
let isRefreshing = false;
let failedQueue = [];
let msalInstance = null;

// Function to set MSAL instance (call this from App.js)
export const setMsalInstance = instance => {
  msalInstance = instance;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to get or refresh token
const getAuthToken = async (forceRefresh = false) => {
  try {
    const token = localStorage.getItem("backend_api_token");
    const tokenExpiry = localStorage.getItem("backend_api_token_expiry");

    if (!token || !tokenExpiry) {
      throw new Error("No authentication token found");
    }

    const now = new Date().getTime();
    const isExpired = now > parseInt(tokenExpiry);

    // If token is about to expire in the next 5 minutes, refresh it
    const willExpireSoon = parseInt(tokenExpiry) - now < 5 * 60 * 1000;

    if (forceRefresh || isExpired || willExpireSoon) {
      if (!msalInstance) {
        throw new Error(
          "MSAL instance not initialized. Call setMsalInstance() first.",
        );
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error("No Microsoft accounts found");
      }

      try {
        // Try silent refresh first
        const silentResponse = await msalInstance.acquireTokenSilent({
          // scopes: ["api://a5451d0f-f4c0-45c0-bf35-254c237be2ca/access_as_user"],
          scopes: ["api://ebcaf93b-866f-450d-86a0-2bf334053481/access_as_user"],
          account: accounts[0],
        });

        // Update stored token
        const expiresIn = silentResponse.expiresIn || 3600;
        const expiryTime = new Date().getTime() + expiresIn * 1000;

        localStorage.setItem("backend_api_token", silentResponse.accessToken);
        localStorage.setItem("backend_api_token_expiry", expiryTime.toString());

        return silentResponse.accessToken;
      } catch (silentError) {
        console.error("Silent token refresh failed:", silentError);

        // If silent fails, try interactive refresh
        try {
          const popupResponse = await msalInstance.acquireTokenPopup({
            // scopes: [
            //   "api://a5451d0f-f4c0-45c0-bf35-254c237be2ca/access_as_user",
            // ],
            scopes: [
              "api://ebcaf93b-866f-450d-86a0-2bf334053481/access_as_user",
            ],
          });

          const expiresIn = popupResponse.expiresIn || 3600;
          const expiryTime = new Date().getTime() + expiresIn * 1000;

          localStorage.setItem("backend_api_token", popupResponse.accessToken);
          localStorage.setItem(
            "backend_api_token_expiry",
            expiryTime.toString(),
          );

          return popupResponse.accessToken;
        } catch (popupError) {
          console.error("Interactive token refresh also failed:", popupError);
          throw new Error("Token refresh failed");
        }
      }
    }

    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw error;
  }
};

// Handle unauthorized access
const handleUnauthorized = () => {
  // Clear all Microsoft auth data
  localStorage.removeItem("microsoft_user");
  localStorage.removeItem("backend_api_token");
  localStorage.removeItem("backend_api_token_expiry");
  localStorage.removeItem("id_token");
  localStorage.removeItem("authProvider");

  toast.info("Your session is expired for Outlook, Please login again");
  // Navigate to dashboard
  if (window.location.pathname !== "/dashboard") {
    // Use window.location for reliable navigation
    window.location.href = "/dashboard";
  }
};

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  async config => {
    try {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      // If we can't get a token, handle unauthorized
      handleUnauthorized();
      return Promise.reject(error);
    }
  },
  error => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle 401 and token refresh
axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // If already refreshing, add to queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        // Try to refresh token
        const newToken = await getAuthToken(true); // Force refresh

        // Process queued requests
        processQueue(null, newToken);

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Token refresh failed
        processQueue(refreshError, null);
        handleUnauthorized();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response?.status === 403) {
      console.error("Forbidden access");
      // You can handle 403 specifically if needed
    }

    // Handle network errors
    if (!error.response) {
      console.error("Network error:", error.message);
    }

    return Promise.reject(error);
  },
);

// Helper function to check if user is authenticated
export const isOutlookAuthenticated = () => {
  const token = localStorage.getItem("backend_api_token");
  const tokenExpiry = localStorage.getItem("backend_api_token_expiry");

  if (!token || !tokenExpiry) {
    return false;
  }

  const now = new Date().getTime();
  const isExpired = now > parseInt(tokenExpiry);

  // Consider token valid if not expired or will expire within 10 minutes
  const willExpireSoon = parseInt(tokenExpiry) - now < 10 * 60 * 1000;

  return !isExpired && !willExpireSoon;
};

// Function to manually refresh token (can be called from components)
export const refreshOutlookToken = async () => {
  try {
    await getAuthToken(true);
    return true;
  } catch (error) {
    console.error("Manual token refresh failed:", error);
    handleUnauthorized();
    return false;
  }
};

// Export the configured axios instance
export { axiosInstance as axios };
